import React from 'react'
import axios from 'axios'
import moment from 'moment'
import { CalendarClock, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import MeetingModal from './meeting_modal'

const STATE_BADGE = {
  scheduled: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-muted text-muted-foreground',
  missed: 'bg-red-100 text-red-700',
  happened: 'bg-emerald-100 text-emerald-800',
  didnt_happen: 'bg-red-100 text-red-700',
  awaiting_response: 'bg-amber-100 text-amber-800'
}

const MISSED_REASON_LABEL = {
  forgot: 'Tutor forgot',
  loadshedding: 'Loadshedding / power outage',
  other: 'Other'
}

function badgeFor(meeting) {
  if (meeting.workflow_state === 'cancelled') return { label: 'Cancelled', style: STATE_BADGE.cancelled }
  if (meeting.outcomes?.some((o) => o.status === 'didnt_happen')) return { label: "Didn't happen", style: STATE_BADGE.didnt_happen }
  if (meeting.outcomes?.some((o) => o.status === 'happened')) return { label: 'Happened', style: STATE_BADGE.happened }
  if (meeting.missed) return { label: 'Missed', style: STATE_BADGE.missed }
  if (moment(meeting.end_time).isBefore(moment())) return { label: 'Awaiting feedback', style: STATE_BADGE.awaiting_response }
  return { label: 'Scheduled', style: STATE_BADGE.scheduled }
}

class LessonPlanMeetings extends React.Component {
  constructor(props) {
    super(props)
    this.state = { meetings: [], loading: true, openMeetingId: null }
  }

  openMeeting = (id) => {
    this.setState({ openMeetingId: id })
  }

  closeMeeting = () => {
    this.setState({ openMeetingId: null })
  }

  componentDidMount() {
    this.fetchMeetings()
  }

  fetchMeetings = () => {
    axios.get(`/api/sales/tutoring_jobs/${this.props.tutoring_job.id}/meetings`)
      .then((response) => {
        this.setState({ meetings: response.data.meetings, loading: false })
      })
      .catch(() => {
        this.setState({ loading: false })
      })
  }

  render() {
    const { meetings, loading } = this.state

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="size-4"/>
            Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <LoaderCircle className="inline-block size-4 animate-spin"/>
            </div>
          )}

          {!loading && meetings.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No meetings yet
            </div>
          )}

          {!loading && meetings.length > 0 && (
            <table className="data-table data-table--compact mb-0 text-sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Participants</th>
                  <th>Outcome</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {meetings.map((meeting) => {
                  const badge = badgeFor(meeting)
                  const tutorOutcome = meeting.outcomes?.find((o) => o.responder_role === 'tutor')
                  const customerOutcome = meeting.outcomes?.find((o) => o.responder_role === 'customer')

                  return (
                    <tr key={meeting.id}>
                      <td className="whitespace-nowrap">
                        {moment(meeting.start_time).format('ddd D MMM YYYY')}
                      </td>
                      <td className="whitespace-nowrap">
                        {moment(meeting.start_time).format('h:mm A')} – {moment(meeting.end_time).format('h:mm A')}
                      </td>
                      <td>
                        {(meeting.participants || []).map((p) => p.name).join(', ')}
                      </td>
                      <td className="text-[13px] text-muted-foreground">
                        {tutorOutcome && (
                          <div className="mb-1.5 last:mb-0">
                            <div>
                              {tutorOutcome.status === 'didnt_happen'
                                ? <span className="text-red-700">Customer missed the meeting{tutorOutcome.missed_reason ? ` (${MISSED_REASON_LABEL[tutorOutcome.missed_reason] || tutorOutcome.missed_reason})` : ''}</span>
                                : <span>Tutor confirmed the meeting{tutorOutcome.rating ? ` · ${tutorOutcome.rating}★` : ''}</span>}
                            </div>
                            {tutorOutcome.feedback && (
                              <div className="mt-0.5 whitespace-pre-wrap italic">"{tutorOutcome.feedback}"</div>
                            )}
                            {tutorOutcome.missed_reason_text && (
                              <div className="mt-0.5 whitespace-pre-wrap italic">"{tutorOutcome.missed_reason_text}"</div>
                            )}
                          </div>
                        )}
                        {customerOutcome && (
                          <div className="mb-1.5 last:mb-0">
                            <div>
                              {customerOutcome.status === 'didnt_happen'
                                ? <span className="text-red-700">Customer said the meeting didn't happen</span>
                                : customerOutcome.feedback
                                  ? <span className="text-red-700">Customer says this tutor isn't a fit</span>
                                  : <span className="text-emerald-700">Customer wants to go ahead with this tutor</span>}
                            </div>
                            {customerOutcome.feedback && (
                              <div className="mt-0.5 whitespace-pre-wrap italic">"{customerOutcome.feedback}"</div>
                            )}
                          </div>
                        )}
                        {!tutorOutcome && !customerOutcome && (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.style}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => this.openMeeting(meeting.id)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </CardContent>

        {this.state.openMeetingId && (
          <MeetingModal
            tutoringJobId={this.props.tutoring_job.id}
            meetingId={this.state.openMeetingId}
            onClose={this.closeMeeting}
            onMeetingChanged={this.fetchMeetings}
          />
        )}
      </Card>
    )
  }
}

export default LessonPlanMeetings
